class CollectionFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.filterData = [];
    this.renderVersion = 0;
    this.productGridId = 'CollectionProductGrid';
    this.filterFormsId = 'CollectionFiltersForm';
    this.collectionTemplate = document.querySelector('#main-body.collection-template');
    this.searchTemplate = document.querySelector('#main-body.search-template');
    this.form = this.querySelector('form');
    this.openButton = this.querySelector('[data-drawer-open-btn]') || document.querySelector('[data-drawer-open-btn]');
    this.closeButton = this.querySelector('[data-drawer-close-btn]');
    this.scrollPosition = 0;

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
    this.handleDrawerClose = this.handleDrawerClose.bind(this);
    this.handleOffDrawerClick = this.handleOffDrawerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 100);
    this.onHistoryChange = this.onHistoryChange.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.debouncedOnSubmit = this.debounce(this.onSubmitHandler.bind(this), 350);

    this.sections = this.getSections();

    if (this.form) this.form.addEventListener('input', this.debouncedOnSubmit);
    if (this.openButton) this.openButton.addEventListener('click', this.handleDrawerOpen);
    if (this.closeButton) this.closeButton.addEventListener('click', this.handleDrawerClose);

    window.addEventListener('resize', this.handleResize, { passive: true });
    window.addEventListener('popstate', this.onHistoryChange);
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleKeydown);

    this.bindActiveFacetButtonEvents();
    this.updateDrawerAccessibility();
  }

  disconnectedCallback() {
    this.renderVersion += 1;
    this.handleDrawerClose({ restoreFocus: false });
    if (this.form) this.form.removeEventListener('input', this.debouncedOnSubmit);
    if (this.openButton) this.openButton.removeEventListener('click', this.handleDrawerOpen);
    if (this.closeButton) this.closeButton.removeEventListener('click', this.handleDrawerClose);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('popstate', this.onHistoryChange);
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('click', this.handleOffDrawerClick, true);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  }

  handleResize() {
    this.updateDrawerAccessibility();
    if (this.classList.contains('is-open')) this.setFilterDrawerPosition();
  }

  updateDrawerAccessibility() {
    if (window.matchMedia('(max-width: 768px)').matches) {
      this.setAttribute('role', 'dialog');
      if (this.classList.contains('is-open')) this.setAttribute('aria-modal', 'true');
      else this.removeAttribute('aria-modal');
      this.setFilterDrawerPosition();
    } else {
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      if (this.classList.contains('is-open')) this.handleDrawerClose({ restoreFocus: false });
    }
  }

  setFilterDrawerPosition() {
    if (!this.form) return;
    const header = document.querySelector('[data-header]');
    const headerHeight = header ? header.getBoundingClientRect().height : 0;
    this.form.style.top = `${Math.max(0, Math.round(headerHeight))}px`;
  }

  handleDrawerOpen(event) {
    if (!window.matchMedia('(max-width: 768px)').matches || !this.form) return;
    event?.preventDefault();

    this.scrollPosition = window.scrollY || window.pageYOffset || 0;
    this.setFilterDrawerPosition();
    this.classList.add('is-open');
    this.setAttribute('aria-modal', 'true');
    this.openButton?.setAttribute('aria-expanded', 'true');

    document.body.classList.add('scroll-locked');
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';

    document.addEventListener('click', this.handleOffDrawerClick, true);
    window.requestAnimationFrame(() => (this.closeButton || this.form.querySelector('button, input, select, a'))?.focus());
  }

  handleDrawerClose(options = {}) {
    if (!this.classList.contains('is-open')) return;

    const restoreFocus = options.restoreFocus !== false;
    this.classList.remove('is-open');
    this.removeAttribute('aria-modal');
    this.openButton?.setAttribute('aria-expanded', 'false');

    document.body.classList.remove('scroll-locked');
    document.body.style.top = '';
    document.body.style.width = '';
    document.removeEventListener('click', this.handleOffDrawerClick, true);

    window.scrollTo(0, this.scrollPosition);
    if (restoreFocus) this.openButton?.focus();
  }

  handleOffDrawerClick(event) {
    if (!this.classList.contains('is-open')) return;
    if (this.form?.contains(event.target) || this.openButton?.contains(event.target)) return;
    this.handleDrawerClose();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      event.preventDefault();
      this.handleDrawerClose();
      return;
    }

    if (event.key !== 'Tab' || !this.classList.contains('is-open') || !this.form) return;
    const focusable = Array.from(this.form.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  handleDocumentClick(event) {
    this.querySelectorAll('details.js-filter[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  }

  onSubmitHandler(event) {
    if (!event?.target?.closest('form')) return;
    event.preventDefault();

    const formData = new FormData(event.target.closest('form'));

    if (this.searchTemplate) {
      const initialParams = new URLSearchParams(window.location.search);
      if (initialParams.get('q')) formData.set('q', initialParams.get('q'));
    }

    this.renderPage(new URLSearchParams(formData).toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget?.href || event.target?.closest('a')?.href;
    if (!href) return;
    this.toggleActiveFacets();
    this.renderPage(new URL(href).searchParams.toString());
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || window.location.search.replace(/^\?/, '');
    this.renderPage(searchParams, null, false);
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
      element.toggleAttribute('aria-disabled', disable);
    });
  }

  renderPage(searchParams, event, updateURLHash = true) {
    const version = ++this.renderVersion;
    this.sections = this.getSections();
    const grid = document.getElementById(this.productGridId);
    grid?.setAttribute('aria-busy', 'true');
    grid?.firstElementChild?.classList.add('loading');

    this.sections.forEach((section) => {
      const separator = searchParams ? '&' : '';
      const url = this.collectionTemplate
        ? `${window.location.pathname}?section_id=${section.section}${separator}${searchParams}`
        : `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
      const cached = this.filterData.find((item) => item.url === url);
      if (cached) this.renderSectionFromCache(cached, section, event);
      else this.renderSectionFromFetch(url, section, event, version, searchParams);
    });

    if (updateURLHash) this.updateURLHash(searchParams);
  }

  async renderSectionFromFetch(url, section, event, version, searchParams) {
    try {
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
      const html = await response.text();
      if (version !== this.renderVersion || !this.isConnected) return;
      this.filterData.push({ html, url });
      if (this.filterData.length > 20) this.filterData.shift();
      this.renderSection(html, section, event);
    } catch (error) {
      if (version !== this.renderVersion || !this.isConnected) return;
      // Use a full page request so URL, filters and products remain consistent.
      window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
      console.error('[Mama Museum] Unable to update collection filters.', error);
      document.getElementById(this.productGridId)?.firstElementChild?.classList.remove('loading');
      this.toggleActiveFacets(false);
    }
  }

  renderSectionFromCache(cacheEntry, section, event) {
    this.renderSection(cacheEntry.html, section, event);
  }

  renderSection(html, section, event) {
    if (section.id === this.productGridId) this.renderProductGrid(html);
    if (section.id === this.filterFormsId) this.renderFilters(html, event);
  }

  renderProductGrid(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById(this.productGridId);
    const target = document.getElementById(this.productGridId);
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
    target.removeAttribute('aria-busy');
  }

  renderFilters(html, event) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incomingFilters = parsed.querySelectorAll(`#${this.filterFormsId} .js-filter`);
    const activeIndex = event?.target?.closest('.js-filter')?.dataset.index;

    incomingFilters.forEach((incoming) => {
      if (incoming.dataset.index === activeIndex) return;
      const current = this.querySelector(`.js-filter[data-index="${CSS.escape(incoming.dataset.index || '')}"]`);
      if (current) current.innerHTML = incoming.innerHTML;
    });

    if (activeIndex) {
      const source = Array.from(incomingFilters).find((item) => item.dataset.index === activeIndex);
      const target = this.querySelector(`.js-filter[data-index="${CSS.escape(activeIndex)}"]`);
      if (source && target) this.renderCounts(source, target);
    }

    this.renderActiveFacets(parsed);
  }

  renderActiveFacets(parsedHTML) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const source = parsedHTML.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) {
        target.innerHTML = source.innerHTML;
        target.style.display = source.style.display;
      }
    });
    this.bindActiveFacetButtonEvents();
    this.toggleActiveFacets(false);
  }

  renderCounts(source, target) {
    ['.count-bubble', '.facets__selected'].forEach((selector) => {
      const sourceElement = source.querySelector(selector);
      const targetElement = target.querySelector(selector);
      if (sourceElement && targetElement) targetElement.outerHTML = sourceElement.outerHTML;
    });
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.removeEventListener('click', this.onActiveFilterClick);
      element.addEventListener('click', this.onActiveFilterClick);
    });
  }

  updateURLHash(searchParams) {
    const url = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;
    history.pushState({ searchParams }, '', url);
  }

  getSections() {
    const grid = document.getElementById(this.productGridId);
    const filters = document.getElementById('main-collection-filters');
    const sections = [];
    if (grid?.dataset.sectionId) sections.push({ id: this.productGridId, section: grid.dataset.sectionId });
    if (filters?.dataset.id) sections.push({ id: this.filterFormsId, section: filters.dataset.id });
    return sections;
  }
}

if (!customElements.get('collection-filtering-form')) {
  customElements.define('collection-filtering-form', CollectionFiltersForm);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = Array.from(this.querySelectorAll('input'));
    this.inputs.forEach((element) => element.addEventListener('change', this.onRangeChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    if (this.inputs.length < 2) return;
    const [minInput, maxInput] = this.inputs;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (!minInput.value) maxInput.setAttribute('min', '0');
    if (!maxInput.value) minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    if (input.value.trim() === '') return;
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (Number.isNaN(value)) return;
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (!customElements.get('price-range-selector')) {
  customElements.define('price-range-selector', PriceRange);
}
